/**
 * Owner: Masa Bayu
 * File: absolute-placement-plugin.tsx
 * Date:Time: 2026-10-13 23:54
 * Description: Edit absolute paint · tip textAlign only (R7 · decide retired).
 */

import { useLayoutEffect, type ReactElement } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { BODY_COLUMN_JUSTIFY_RATIO } from '@qx/qx-justify';
import { paintTextFromTip } from '@qx/qx-render';
import { getRenderPipe } from '../feature-flags';
import type { UniversalAtom } from '../types';
import {
  facePhysicalLineEvidence,
  faceTextAbsencePaint,
  faceTextWrapPaint,
} from '../urp/face-text-paint';
import {
  buildEditJustifyGroupPaint,
  type EditJustifyGroupRole,
} from './edit-justify-group-paint';

function hasWitnessedBounds(atom: UniversalAtom): boolean {
  return atom.bbox.width > 0 && atom.bbox.height > 0;
}

/** Header then body — same order as populateLexicalRootFromStructuredPage. */
export function textAtomsForEditPlacement(
  page: {
    readonly headerLayer: readonly UniversalAtom[];
    readonly bodyLayer: readonly UniversalAtom[];
  },
): readonly UniversalAtom[] {
  return [
    ...page.headerLayer.filter((a) => a.type === 'text'),
    ...page.bodyLayer.filter((a) => a.type === 'text'),
  ];
}

/**
 * PAGE04 witnessed a multi-line body column (uniform right edge across many
 * physical lines). Cover imprint pairs (2 scale-gate atoms) are not that column.
 * Paint-only · not a semantic layer invent.
 */
export const MIN_EDIT_JUSTIFY_COLUMN_LINES = 3;

/** Re-export SoT threshold · @qx/qx-justify (geometry-only · A5). */
export const EDIT_BODY_COLUMN_JUSTIFY_RATIO = BODY_COLUMN_JUSTIFY_RATIO;

/** Ink reaches column right · ε pt (columnRight − 2). */
export const EDIT_BODY_COLUMN_INK_EPS_PT = 2;

/**
 * Book-body scale gate (not cover display / drop-cap large type).
 * Alone is insufficient — column peer count must also clear
 * {@link MIN_EDIT_JUSTIFY_COLUMN_LINES} via {@link bodyJustifyColumnRight}.
 *
 * P03-W05-W06-PAINT-GATE-FIX-01 · witnessed textAlign=center (asCenteredTitle
 * title plate) must never enter body-justify paint · no page/string invent.
 */
export function isEditJustifyBodyAtom(
  atom: UniversalAtom,
  bodyIds: ReadonlySet<string>,
): boolean {
  if (atom.type !== 'text' || !bodyIds.has(atom.id)) return false;
  if (!hasWitnessedBounds(atom)) return false;
  if (atom.textAlign === 'center') return false;
  const size = atom.font?.size ?? 99;
  return size <= 14 && atom.bbox.height <= 18;
}

/** Count body atoms that pass the scale gate (pre-column). */
export function countEditJustifyBodyScaleAtoms(
  atoms: readonly UniversalAtom[],
  bodyIds: ReadonlySet<string>,
): number {
  let n = 0;
  for (const atom of atoms) {
    if (isEditJustifyBodyAtom(atom, bodyIds)) n += 1;
  }
  return n;
}

/**
 * Provisional column edges from scale-gate peers (no fill witness yet).
 */
function provisionalBodyColumnEdges(
  atoms: readonly UniversalAtom[],
  bodyIds: ReadonlySet<string>,
): { left: number; right: number } | null {
  if (
    countEditJustifyBodyScaleAtoms(atoms, bodyIds) <
    MIN_EDIT_JUSTIFY_COLUMN_LINES
  ) {
    return null;
  }
  let maxRight: number | null = null;
  let minLeft: number | null = null;
  for (const atom of atoms) {
    if (!isEditJustifyBodyAtom(atom, bodyIds)) continue;
    const right = atom.bbox.x + atom.bbox.width;
    if (maxRight === null || right > maxRight) maxRight = right;
    if (minLeft === null || atom.bbox.x < minLeft) minLeft = atom.bbox.x;
  }
  if (maxRight === null || minLeft === null) return null;
  return { left: minLeft, right: maxRight };
}

/**
 * Shared plate witness · ≥2 scale-gate peers tip-fill the provisional column.
 * One max-peer alone is a tautology (always fills) — cover short-only stays
 * null (local Page1 Pass). Prakata multi-line plate → ≥2 fills → FIX-02 path.
 */
export function bodyJustifyColumnHasFillWitness(
  atoms: readonly UniversalAtom[],
  bodyIds: ReadonlySet<string>,
  columnRight: number,
  bodyLeft: number,
): boolean {
  let nearFull = 0;
  for (const atom of atoms) {
    if (!isEditJustifyBodyAtom(atom, bodyIds)) continue;
    if (editBodyColumnNearFull(atom, columnRight, bodyLeft)) {
      nearFull += 1;
      if (nearFull >= 2) return true;
    }
  }
  return false;
}

/**
 * Witnessed column right from sibling body-justify atoms (max x+w).
 * Null when peers < {@link MIN_EDIT_JUSTIFY_COLUMN_LINES} OR no tip-fill
 * witness — replicates COVER-TEXT-WRAP / Page1 regression Pass on cover.
 */
export function bodyJustifyColumnRight(
  atoms: readonly UniversalAtom[],
  bodyIds: ReadonlySet<string>,
): number | null {
  const edges = provisionalBodyColumnEdges(atoms, bodyIds);
  if (!edges) return null;
  if (
    !bodyJustifyColumnHasFillWitness(
      atoms,
      bodyIds,
      edges.right,
      edges.left,
    )
  ) {
    return null;
  }
  return edges.right;
}

/**
 * Witnessed body column left (min x among scale-gate justify peers).
 * Same fill-witness gate as {@link bodyJustifyColumnRight}.
 */
export function bodyJustifyColumnLeft(
  atoms: readonly UniversalAtom[],
  bodyIds: ReadonlySet<string>,
): number | null {
  const edges = provisionalBodyColumnEdges(atoms, bodyIds);
  if (!edges) return null;
  if (
    !bodyJustifyColumnHasFillWitness(
      atoms,
      bodyIds,
      edges.right,
      edges.left,
    )
  ) {
    return null;
  }
  return edges.left;
}

/**
 * R7 · tip textAlign witness for paint · no invent.
 */
export function tipTextAlignIsJustify(atom: UniversalAtom): boolean {
  return atom.textAlign === 'justify';
}

/**
 * Geometry column-near-full · layout width only · not textAlign invent.
 * Geometry-only · not tip textAlign invent (R7).
 */
export function editBodyColumnNearFull(
  atom: UniversalAtom,
  columnRight: number,
  bodyLeft: number,
): boolean {
  const lineRight = atom.bbox.x + atom.bbox.width;
  const columnWidth = Math.max(1, columnRight - bodyLeft);
  const ratio = (lineRight - bodyLeft) / columnWidth;
  const reachesRight = lineRight >= columnRight - EDIT_BODY_COLUMN_INK_EPS_PT;
  return ratio >= EDIT_BODY_COLUMN_JUSTIFY_RATIO || reachesRight;
}

/**
 * Stretch width role for Edit body paint group.
 * Paint group role wins when present: middle → stretch · final → left.
 * Ungrouped → tip textAlign === justify (G16).
 */
export function editBodyStretchJustifyPaint(
  atom: UniversalAtom,
  _columnRight?: number,
  _bodyLeft?: number,
  groupRole?: EditJustifyGroupRole | null,
): boolean {
  if (groupRole === 'final') return false;
  if (groupRole === 'middle') return true;
  return tipTextAlignIsJustify(atom);
}

function applyAbsenceStyles(
  node: HTMLElement,
  absence: ReturnType<typeof faceTextAbsencePaint>,
): void {
  if (absence.opacity !== 1) {
    node.style.opacity = String(absence.opacity);
  } else {
    node.style.removeProperty('opacity');
  }
  if (absence.visibility === false) {
    node.style.visibility = 'hidden';
  } else {
    node.style.removeProperty('visibility');
  }
  if (absence.rotation !== 0) {
    node.style.transform = `rotate(${absence.rotation}deg)`;
  } else {
    node.style.removeProperty('transform');
  }
}

function applyWrapStyles(
  node: HTMLElement,
  wrap: ReturnType<typeof faceTextWrapPaint>,
): void {
  if (wrap.whiteSpace !== undefined) {
    node.style.whiteSpace = String(wrap.whiteSpace);
  } else {
    node.style.removeProperty('white-space');
  }
  if (wrap.overflowWrap !== undefined) {
    node.style.overflowWrap = String(wrap.overflowWrap);
  } else {
    node.style.removeProperty('overflow-wrap');
  }
  if (wrap.wordBreak !== undefined) {
    node.style.wordBreak = String(wrap.wordBreak);
  } else {
    node.style.removeProperty('word-break');
  }
  if (wrap.overflow !== undefined) {
    node.style.overflow = String(wrap.overflow);
  } else {
    node.style.removeProperty('overflow');
  }
  node.setAttribute('data-urp-wrap', wrap.wrapPolicy);
  if (wrap.dataWrapPolicy) {
    node.setAttribute('data-wrap-policy', wrap.dataWrapPolicy);
  } else {
    node.removeAttribute('data-wrap-policy');
  }
}

function applyJustifyBodyPaint(
  node: HTMLElement,
  atom: UniversalAtom,
  columnRight: number,
  bodyLeft: number,
  groupRole: EditJustifyGroupRole | null,
  /** Group-local tip max right · falls back to page columnRight. */
  groupPaintRight?: number,
): void {
  const paintRight = groupPaintRight ?? columnRight;
  const columnWidth = Math.max(0, paintRight - atom.bbox.x);
  const paintWidth = Math.max(atom.bbox.width, columnWidth);
  const pipe = getRenderPipe();
  void bodyLeft;
  /*
   * R11.7 · Face abs (QxPagedRenderer) owns default v2 edit.
   * Lexical AbsolutePlacement remains for env v1/legacy only · file kept (R11.8).
   */
  if (pipe === 'v2') {
    node.style.position = 'absolute';
    node.style.left = `${atom.bbox.x}px`;
    node.style.top = `${atom.bbox.y}px`;
    node.style.width = `${atom.bbox.width}px`;
    node.style.height = `${atom.bbox.height}px`;
    return;
  }

  node.style.position = 'absolute';
  node.style.left = `${atom.bbox.x}px`;
  node.style.top = `${atom.bbox.y}px`;
  node.style.width = `${paintWidth}px`;
  node.style.height = `${atom.bbox.height}px`;
  node.style.margin = '0';
  node.style.boxSizing = 'border-box';
  /*
   * P02-IMPRINT-FIX-02 · each tip atom is one physical line.
   * nowrap + tip-cell line-height · Tip bbox unchanged.
   */
  node.style.whiteSpace = 'nowrap';
  node.style.overflowWrap = 'normal';
  node.style.wordBreak = 'normal';
  node.style.overflow = 'visible';
  node.style.lineHeight = `${atom.bbox.height}px`;

  /* R7 · read tip · no invent · v1/legacy Lexical only after R11.7. */
  const tipAlign = atom.textAlign ?? 'left';
  const paint = paintTextFromTip({
    element: {
      id: atom.id,
      content: atom.textContent ?? '',
      bbox: atom.bbox,
      style: {
        fontFamily: atom.font?.family ?? '',
        fontSize: atom.font?.size ?? atom.bbox.height,
        textAlign: tipAlign,
      },
    },
    page: { width: 0, height: 0 },
  });
  node.style.textAlign = paint.textAlign;
  if (paint.textAlignLast !== undefined) {
    node.style.textAlignLast = paint.textAlignLast;
  } else {
    node.style.textAlignLast = tipAlign === 'center' ? 'center' : 'left';
  }
  const stretchJustify =
    groupRole === 'final'
      ? false
      : groupRole === 'middle'
        ? true
        : tipAlign === 'justify';

  if (atom.font?.family) {
    node.style.fontFamily = atom.font.family;
  }
  if (atom.font?.size) {
    node.style.fontSize = `${atom.font.size}px`;
  }
  if (atom.font?.weight) {
    node.style.fontWeight = String(atom.font.weight);
  }
  if (atom.color) {
    node.style.color = atom.color;
  }

  node.setAttribute('data-element-id', atom.id);
  node.setAttribute('data-placement-mode', 'absolute');
  node.setAttribute('data-placement-x', String(atom.bbox.x));
  node.setAttribute('data-placement-y', String(atom.bbox.y));
  node.setAttribute('data-urp-wrap', 'EDIT_JUSTIFY_BODY');
  node.setAttribute('data-urp-policy-source', 'qx-urp');
  node.setAttribute('data-render-pipe', pipe);
  /* Physical tip line · nowrap (FIX-02) · still edit-justify body role. */
  node.setAttribute('data-wrap-policy', 'edit-justify-body-nowrap');
  node.setAttribute('data-edit-justify-fix-01', '1');
  node.setAttribute('data-p02-imprint-fix-02', '1');
  node.setAttribute(
    'data-edit-justify-short',
    stretchJustify ? '0' : '1',
  );
  if (groupRole) {
    node.setAttribute('data-edit-justify-group-role', groupRole);
  } else {
    node.removeAttribute('data-edit-justify-group-role');
  }
}

function applyPhysicalLinePaint(
  node: HTMLElement,
  atom: UniversalAtom,
  preserveSource: boolean,
): void {
  const placementMode = 'ABSOLUTE' as const;
  const physicalLine = facePhysicalLineEvidence(
    preserveSource,
    placementMode,
  );
  const wrap = faceTextWrapPaint({
    preserveSource,
    placementMode,
    physicalLine,
  });
  const absence = faceTextAbsencePaint({});

  node.style.position = 'absolute';
  node.style.left = `${atom.bbox.x}px`;
  node.style.top = `${atom.bbox.y}px`;
  node.style.width = `${atom.bbox.width}px`;
  node.style.height = `${atom.bbox.height}px`;
  node.style.margin = '0';
  node.style.boxSizing = 'border-box';

  applyWrapStyles(node, wrap);
  applyAbsenceStyles(node, absence);

  /* COVER-TEXTALIGN-FIX-01 · witnessed only · never invent default. */
  if (atom.textAlign !== undefined) {
    node.style.textAlign = atom.textAlign;
  }

  node.setAttribute('data-element-id', atom.id);
  node.setAttribute('data-placement-mode', 'absolute');
  node.setAttribute('data-placement-x', String(atom.bbox.x));
  node.setAttribute('data-placement-y', String(atom.bbox.y));
  node.setAttribute('data-urp-policy-source', 'qx-urp');
  node.removeAttribute('data-edit-justify-fix-01');
  node.removeAttribute('data-edit-justify-short');
}

function applyAbsoluteParagraphStyles(
  root: HTMLElement,
  atoms: readonly UniversalAtom[],
  bodyIds: ReadonlySet<string>,
  preserveSource: boolean,
): void {
  const columnRight = bodyJustifyColumnRight(atoms, bodyIds);
  const bodyLeft = bodyJustifyColumnLeft(atoms, bodyIds);
  const justifyPeers =
    columnRight !== null && bodyLeft !== null
      ? atoms.filter((a) => isEditJustifyBodyAtom(a, bodyIds))
      : [];
  const groupPaint =
    columnRight !== null && bodyLeft !== null
      ? buildEditJustifyGroupPaint(justifyPeers, bodyLeft, columnRight)
      : null;
  const paragraphs = root.querySelectorAll(':scope > p');
  paragraphs.forEach((node, i) => {
    const atom = atoms[i];
    if (!(node instanceof HTMLElement) || !atom) return;
    if (!hasWitnessedBounds(atom)) return;

    if (
      columnRight !== null &&
      bodyLeft !== null &&
      isEditJustifyBodyAtom(atom, bodyIds)
    ) {
      applyJustifyBodyPaint(
        node,
        atom,
        columnRight,
        bodyLeft,
        groupPaint?.roles.get(atom.id) ?? null,
        groupPaint?.paintRightById.get(atom.id),
      );
      applyAbsenceStyles(node, faceTextAbsencePaint({}));
      return;
    }
    applyPhysicalLinePaint(node, atom, preserveSource);
  });
}

/**
 * EM-FIX-01 · paint-only. Does not change Lexical text / tip semantics.
 * QX-URP-04 · cover absolute = physical-line nowrap.
 * PV-PAGE04-EDIT-JUSTIFY-FIX-01 · body book lines = column width + justify.
 */
export function AbsolutePlacementPlugin(props: {
  readonly atoms: readonly UniversalAtom[];
  /** Body layer ids · justify paint only for book-body atoms. */
  readonly bodyAtomIds?: ReadonlySet<string>;
  /** Default true · Edit absolute path is preserve-source Face. */
  readonly preserveSource?: boolean;
}): ReactElement | null {
  const {
    atoms,
    bodyAtomIds = new Set<string>(),
    preserveSource = true,
  } = props;
  const [editor] = useLexicalComposerContext();

  useLayoutEffect(() => {
    const paint = () => {
      const root = editor.getRootElement();
      if (!root) return;
      applyAbsoluteParagraphStyles(
        root,
        atoms,
        bodyAtomIds,
        preserveSource,
      );
    };
    paint();
    return editor.registerUpdateListener(() => {
      paint();
    });
  }, [editor, atoms, bodyAtomIds, preserveSource]);

  return null;
}
